/**
 * Calendar dates for the API, taken from the reader's own calendar.
 *
 * Every `*_date` Kontora holds is a day, not an instant (`model.md` §1.5), so
 * "today" has to mean the day on the wall where somebody is sitting.
 * `toISOString()` answers in UTC instead: in Berlin between midnight and one
 * (two in summer) it is still yesterday there, and a booking entered at 00:30
 * on the 1st lands in the previous month's budget.
 *
 * So these read the local calendar fields and never go through UTC.
 */
export function isoDate(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Today, as the reader's calendar has it. */
export function today(): string {
  return isoDate(new Date());
}

/**
 * The ISO weekday of a calendar date, 1 for Monday to 7 for Sunday — the
 * numbering a weekly rule's anchor uses. Read in UTC because the string is a
 * day, not an instant; a local reading would put it on the previous day west of
 * London.
 */
export function isoWeekday(iso: string): number {
  const [year, month, day] = iso.split("-").map(Number);
  const weekday = new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1)).getUTCDay();
  return weekday === 0 ? 7 : weekday;
}

/** The day of the month of a calendar date, 1 to 31. */
export function dayOfMonth(iso: string): number {
  return Number(iso.split("-")[2]);
}
